import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GridOnIcon from '@mui/icons-material/GridOn';
import FlagIcon from '@mui/icons-material/Flag';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { colorForSignal, formatValue, formatDateUTC } from './studioUtils';

/**
 * The data grid - the thing the whole studio is really about.
 *
 * It mirrors the real segment dataframe: one row per time bucket, one column
 * per signal, sorted by time. The hard guarantee is that NO cell ever shows
 * NaN / null / "N/A": the sanitiser upstream fills every gap, and here we
 * merely render those filled cells slightly muted (with a "~" affordance and
 * a tooltip) so the user can trust what's real vs. inferred without ever
 * staring at a hole in their data.
 */
const HeaderCell = ({ column, color }) => {
  const theme = useTheme();
  const coveragePct = Math.round(column.coverage * 100);
  const low = coveragePct < 60;
  return (
    <TableCell
      sx={{
        fontWeight: 700,
        whiteSpace: 'nowrap',
        borderBottom: `2px solid ${color}`,
        bgcolor: 'background.paper',
        top: 0,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        {column.isLabel && (
          <Tooltip title="Target (what you're predicting)">
            <FlagIcon sx={{ fontSize: 13, color: theme.palette.warning.main }} />
          </Tooltip>
        )}
        {column.marketplace && (
          <Tooltip title={column.marketplace.listing_title || 'Marketplace signal'}>
            <StorefrontIcon sx={{ fontSize: 13, color: 'primary.main' }} />
          </Tooltip>
        )}
        <Box component="span">{column.label}</Box>
        <Tooltip
          title={`${column.realCount} of ${column.total} points are real readings; the rest were gap-filled.`}
        >
          <Chip
            size="small"
            label={`${coveragePct}%`}
            sx={{
              height: 16,
              fontSize: '0.6rem',
              fontWeight: 700,
              color: low ? theme.palette.warning.main : theme.palette.success.main,
              bgcolor: alpha(
                low ? theme.palette.warning.main : theme.palette.success.main,
                0.14,
              ),
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
        </Tooltip>
      </Stack>
    </TableCell>
  );
};

const LiveDataGrid = ({ sanitized, colorMap, maxHeight = 360 }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  if (!sanitized || sanitized.columns.length === 0) {
    return (
      <Box
        sx={{
          height: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">Your data table will appear here.</Typography>
      </Box>
    );
  }

  const { timestamps, columns, totalRows } = sanitized;
  const shown = timestamps.length;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, pb: 0.75 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <GridOnIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Data grid
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {columns.length} columns · showing first {shown.toLocaleString()} of{' '}
            {totalRows.toLocaleString()} rows
          </Typography>
        </Stack>
        <Tooltip title="Cells marked ~ were filled in to keep the dataset complete. No gaps reach your model.">
          <Chip
            size="small"
            label={sanitized.hadGaps ? 'Gaps filled · 0 empty cells' : 'Complete · 0 empty cells'}
            sx={{
              height: 20,
              fontSize: '0.66rem',
              fontWeight: 700,
              color: theme.palette.success.main,
              bgcolor: alpha(theme.palette.success.main, 0.14),
            }}
          />
        </Tooltip>
      </Stack>

      <TableContainer
        sx={{
          maxHeight,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          '&::-webkit-scrollbar': { height: 10, width: 10 },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.text.primary, 0.2),
            borderRadius: 8,
          },
        }}
      >
        <Table stickyHeader size="small" sx={{ '& td, & th': { fontSize: '0.78rem' } }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  minWidth: 170,
                  bgcolor: 'background.paper',
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                }}
              >
                Timestamp (UTC)
              </TableCell>
              {columns.map((col) => (
                <HeaderCell
                  key={col.key}
                  column={col}
                  color={colorMap[col.key] || colorForSignal(col.key)}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {timestamps.map((ts, rowIdx) => (
              <TableRow
                key={rowIdx}
                hover
                sx={{
                  '&:nth-of-type(odd)': {
                    bgcolor: alpha(theme.palette.text.primary, dark ? 0.02 : 0.015),
                  },
                }}
              >
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    color: 'text.secondary',
                    position: 'sticky',
                    left: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    borderRight: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {formatDateUTC(ts, true)}
                </TableCell>
                {columns.map((col) => {
                  const filled = col.mask[rowIdx];
                  return (
                    <TableCell
                      key={col.key}
                      align={col.isNumeric ? 'right' : 'left'}
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: filled ? 'text.disabled' : 'text.primary',
                        fontStyle: filled ? 'italic' : 'normal',
                      }}
                    >
                      {filled ? (
                        <Tooltip title="Gap-filled to keep the series continuous">
                          <Box component="span" sx={{ borderBottom: `1px dotted ${theme.palette.divider}` }}>
                            {formatValue(col.values[rowIdx])}
                            <Box component="span" sx={{ ml: 0.25, opacity: 0.5 }}>~</Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        formatValue(col.values[rowIdx])
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LiveDataGrid;
